import dayjs from "dayjs";
import { Steps, Popconfirm, Descriptions, Upload, Button, Divider, notification, Card } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { isOctaPharmaProgram } from "@/libs/util";
import { ProgramName } from "@/types/global";
import { useEffect, useState } from "react";
import useGlobalConfig from "@/store/global";
import programConfig from "@/config/programs/formFields";
import useDeleteEOB from "@/hooks/useDeleteEOB";
import { useQueryClient } from "@tanstack/react-query";
import useDeleteHICF from "@/hooks/useDeleteHICF";

const AssistanceRequestDetail = ({ data, program }: { data: any, program: ProgramName }) => {
  const isOctaPharma = isOctaPharmaProgram(program);
  const config = (programConfig as Record<ProgramName, { fields: any[] }>)[program] || { fields: [] };

  const [fileList, setFileList] = useState<any[]>([]);
  const [hicfFileList, setHicfFileList] = useState<any[]>([]);

  const { meData } = useGlobalConfig();
  const queryClient = useQueryClient();

  const [toast, contextHolder] = notification.useNotification();

  const { mutateAsync: deleteEOB } = useDeleteEOB({ id: data.id });
  const { mutateAsync: deleteHICF } = useDeleteHICF({ id: data.id });

  const renderField = (field: any) => {
    if (field.name === "patient_gender_code") {
      return data.gender_string;
    }

    if (field.name === "previous_product_name") {
      return data[field.name] === "1" ? "YES" : "NO"
    }

    if (typeof data[field.name] === "boolean") {
      return data[field.name] ? "YES" : "NO"
    }

    if (typeof data[field.name] === 'number') {
      return data[field.name];
    }

    if (data[field.name] && typeof data[field.name] === 'string') {
      const dateRegex = /^\d{4}(-|\/)\d{2}(-|\/)\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:\d{2}|Z)?)?$/;
      const value = data[field.name];

      if ((dateRegex.test(value) || value.includes('T')) && dayjs(value).isValid()) {
        return dayjs(value).format("MM-DD-YYYY");
      }
    }

    return data[field.name]
  }

  const confirmDelete = async (id: number) => {
    await axios.delete(`assistance_requests/${id}`);
    window.location.href = "/patients";
  };

  const changeFormAttachmentIds = (e: any) => {
    setFileList(e.fileList);

    if (e.file.status === 'done') {
      queryClient.invalidateQueries({
        queryKey: ["assistance-request-detail", data.id?.toString()]
      });
    }
  };

  const changeHicfFormAttachmentIds = (e: any) => {
    setHicfFileList(e.fileList);

    if (e.file.status === 'done') {
      queryClient.invalidateQueries({
        queryKey: ["assistance-request-detail", data.id?.toString()]
      });
    }
  };

  const handleRemoveHICF = async () => {
    try {
      await deleteHICF()
      toast.success({
        message: "HICF deleted successfully",
        description: "The HICF has been deleted successfully.",
      });
    } catch (error) {
      toast.error({
        message: "Failed to delete HICF",
        description: "The HICF could not be deleted. Please try again.",
      });
    }
  };

  const handleRemoveEOB = async () => {
    try {
      await deleteEOB();
      toast.success({
        message: "EOB deleted successfully",
        description: "The EOB has been deleted successfully.",
      });
    } catch (error) {
      toast.error({
        message: "Failed to delete EOB",
        description: "The EOB could not be deleted. Please try again.",
      });
    }
  };


  const visibleFields = config.fields.filter((field: any) => {
    if (field.hidden) {
      if (typeof field.hidden === 'function') {
        return !field.hidden(meData);
      }
      return !field.hidden;
    }
    return true;
  });

  useEffect(() => {
    setFileList(data.eob_url ? [{
      uid: "1",
      name: data.eob_filename,
      url: data.eob_url,
    }] : []);

    setHicfFileList(data.hicf_url ? [{
      uid: "1",
      name: data.hicf_filename,
      url: data.hicf_url,
    }] : []);
  }, [data]);

  return (
    <>
      {contextHolder}
      <div className="mx-auto max-w-4xl p-6">
        <Steps
          className="my-8"
          current={data.status === undefined ? "-1" : data.status}
          items={[
            {
              title: "EOB Pending",
              status: data.status === 0 ? "finish" : undefined,
            },
            {
              title: "EOB in Review",
              status: data.status === 1 ? "finish" : undefined,
            },
            {
              title: "EOB Paid",
              status: data.status === 2 ? "finish" : undefined,
            },
          ]}
        />
      </div>
      <Card>
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-lg md:text-xl font-bold uppercase">Request Details</h2>
          <div>
            <Popconfirm
              title="Delete the Request"
              description="Are you sure to delete this Request?"
              onConfirm={() => confirmDelete(data.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                style={{ backgroundColor: "#d9534f", color: "white" }}
              >
                Delete Request
              </Button>
            </Popconfirm>
          </div>
        </div>

        <Descriptions bordered column={1}>
          <Descriptions.Item styles={{ label: { width: '60%'}}} label={isOctaPharma ? "EOB + HICF (CMS 1500)" : "EOB"}>
            <div className="flex flex-col gap-y-2">
              <Upload
                action={`${axios.defaults.baseURL}/assistance_requests/${data.id}/upload_eob`}
                listType="text"
                fileList={fileList}
                maxCount={1}
                disabled={data.status === 2}
                onRemove={handleRemoveEOB}
                onChange={changeFormAttachmentIds}
                headers={{
                  Authorization: `${localStorage.getItem("Authorization")}`,
                }}
              >
                <Button
                  className="mr-2 rounded bg-[#5bc0de] text-white"
                  icon={<UploadOutlined />}
                >
                  {fileList.length > 0 ? "Replace EOB" : "Upload EOB"}
                </Button>
              </Upload>
              {isOctaPharma && (
                <Upload
                  action={`${axios.defaults.baseURL}/assistance_requests/${data.id}/upload_hicf`}
                  listType="text"
                  maxCount={1}
                  disabled={data.status === 2}
                  fileList={hicfFileList}
                  onRemove={handleRemoveHICF}
                  onChange={changeHicfFormAttachmentIds}
                  name="hicf_file"
                  headers={{
                    ProgramName: "wilate",
                    Authorization: `${localStorage.getItem("Authorization")}`,
                  }}
                >
                  <Button
                    className="mr-2 rounded bg-[#5bc0de] text-white"
                    icon={<UploadOutlined />}
                  >
                    {hicfFileList.length > 0 ? "Replace HICF" : "Upload HICF"}
                  </Button>
                </Upload>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item styles={{ label: { width: '60%'}}}  label="Status">
            {data.status_string}
          </Descriptions.Item>

          {visibleFields.map((field: any) => (
            <Descriptions.Item styles={{ label: { width: '60%'}}}  key={field.name} label={field.label}>
              {renderField(field)}
            </Descriptions.Item>
          ))}
          <Descriptions.Item styles={{ label: { width: '60%'}}}  label="Created on">
            {dayjs(data.created_at).format("MM-DD-YYYY")}
          </Descriptions.Item>
          <Descriptions.Item styles={{ label: { width: '60%'}}}  label="Updated on">
            {dayjs(data.updated_at).format("MM-DD-YYYY")}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </>
  );
};

export default AssistanceRequestDetail;
